import { useState } from 'react'
import {
  truncateBleDataForDisplay,
  type BleDeviceData,
  type BleDeviceSnapshot,
  type BleStreamBase,
} from '@versports/ble-contracts'

interface BleDeviceCardProps {
  device: BleDeviceSnapshot
  disabled?: boolean
  onConnect: (device: BleDeviceSnapshot) => void
  onDisconnect: (device: BleDeviceSnapshot) => void
}

type DataViewMode = 'structured' | 'raw' | 'none'

const HIDDEN_DATA_FIELDS = new Set(['raw_hex', 'raw_len', 'characteristic_uuid'])

const FIELD_LABELS: Record<string, string> = {
  adc_raw: 'ADC raw',
  voltage: 'Voltaje',
  state_counter: 'Contador',
  heartrate: 'Frecuencia cardíaca',
  rr_intervals: 'Intervalos RR',
  samples: 'Muestras',
  sample_count: 'Cant. muestras',
  timestamp: 'Timestamp',
  samples_truncated: 'Truncado',
}

function formatTimestamp(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = Date.parse(value)
  if (Number.isNaN(parsed)) return value
  return new Date(parsed).toLocaleString('es-AR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  })
}

function statusLabel(device: BleDeviceSnapshot): string {
  if (device.status) return device.status
  return device.connected ? 'connected' : 'disconnected'
}

function fieldLabel(key: string): string {
  return FIELD_LABELS[key] ?? key.replace(/_/g, ' ')
}

function formatFieldValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'sí' : 'no'
  if (typeof value === 'number') {
    if (key === 'voltage') return `${value.toFixed(4)} V`
    if (key === 'heartrate') return `${value} BPM`
    return String(value)
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]'
    if (key === 'rr_intervals') {
      const preview = value.slice(0, 4).join(', ')
      return value.length > 4 ? `${preview}… (${value.length})` : preview
    }
    if (key === 'samples') {
      const preview = value.slice(0, 5).join(', ')
      return value.length > 5 ? `${preview}… (${value.length})` : preview
    }
    return `[${value.length} items]`
  }
  return String(value)
}

function streamEntries(data: BleDeviceData): Array<[string, BleStreamBase]> {
  return Object.entries(data).filter(
    (entry): entry is [string, BleStreamBase] =>
      entry[1] !== undefined && typeof entry[1] === 'object',
  )
}

function stripHiddenFields(data: BleDeviceData): BleDeviceData {
  const out: BleDeviceData = {}
  for (const [stream, payload] of Object.entries(data)) {
    if (!payload || typeof payload !== 'object') continue
    const cleaned: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(payload)) {
      if (!HIDDEN_DATA_FIELDS.has(key)) cleaned[key] = value
    }
    out[stream] = cleaned as BleStreamBase
  }
  return out
}

function BleStreamFieldGrid({ stream }: { stream: BleStreamBase }) {
  const fields = Object.entries(stream).filter(
    ([key]) => key !== 'updated_at' && !HIDDEN_DATA_FIELDS.has(key),
  )

  if (fields.length === 0) {
    return <div className="text-xs text-text-muted">Sin campos adicionales.</div>
  }

  return (
    <div className="mt-xs grid grid-cols-1 gap-xs">
      {fields.map(([key, value]) => (
        <div
          key={key}
          className="flex items-start justify-between gap-sm rounded-sm bg-surface-elevated/60 px-xs py-[2px] text-[11px]"
        >
          <span className="shrink-0 text-text-muted">{fieldLabel(key)}</span>
          <span className="break-all text-right font-mono text-text-secondary">
            {formatFieldValue(key, value)}
          </span>
        </div>
      ))}
    </div>
  )
}

function BleStructuredDataView({ data }: { data: BleDeviceData }) {
  const entries = streamEntries(data)

  if (entries.length === 0) {
    return <div className="text-xs text-text-muted">Sin streams con datos.</div>
  }

  return (
    <div className="mt-xs flex max-h-48 flex-col gap-xs overflow-auto">
      {entries.map(([streamName, stream]) => (
        <div
          key={streamName}
          className="rounded-md border border-surface-muted bg-surface-elevated/40 px-sm py-xs"
        >
          <div className="flex flex-wrap items-center justify-between gap-xs">
            <span className="tag shrink-0 uppercase">{streamName}</span>
            <span className="text-[10px] text-text-muted">
              {formatTimestamp(stream.updated_at)}
            </span>
          </div>
          <BleStreamFieldGrid stream={stream} />
        </div>
      ))}
    </div>
  )
}

function DataViewSelector({
  mode,
  onChange,
}: {
  mode: DataViewMode
  onChange: (mode: DataViewMode) => void
}) {
  return (
    <div className="flex flex-wrap gap-xs" role="group" aria-label="Vista de data">
      <button
        type="button"
        className={
          mode === 'none' ? 'btn btn-sm flex-1' : 'btn-ghost btn-sm flex-1'
        }
        onClick={() => onChange('none')}
      >
        Not Show
      </button>
      <button
        type="button"
        className={
          mode === 'structured' ? 'btn btn-sm flex-1' : 'btn-ghost btn-sm flex-1'
        }
        onClick={() => onChange('structured')}
      >
        Estructurado
      </button>
      <button
        type="button"
        className={mode === 'raw' ? 'btn btn-sm flex-1' : 'btn-ghost btn-sm flex-1'}
        onClick={() => onChange('raw')}
      >
        Raw
      </button>
    </div>
  )
}

export function BleDeviceCard({
  device,
  disabled,
  onConnect,
  onDisconnect,
}: BleDeviceCardProps) {
  const [dataViewMode, setDataViewMode] = useState<DataViewMode>('none')
  const title = device.name?.trim() || device.mac_address
  const streams = device.streams?.length
    ? device.streams
    : device.active_streams ?? []
  const displayData = stripHiddenFields(truncateBleDataForDisplay(device.data, 5))
  const hasData = Object.keys(displayData).length > 0
  const lastUpdate = device.updated_at ?? device.last_seen

  return (
    <div className="card flex flex-col gap-sm border-primary/30">
      <div className="flex items-start justify-between gap-sm">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold">{title}</div>
          <div className="truncate text-xs text-text-muted">{device.mac_address}</div>
        </div>
        <span className={device.connected ? 'tag-ok shrink-0' : 'tag shrink-0'}>
          {statusLabel(device)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-md gap-y-xs text-xs text-text-secondary">
        <span>Tipo</span>
        <span className="text-right">{device.device_type ?? '—'}</span>
        <span>RSSI</span>
        <span className="text-right">
          {typeof device.rssi === 'number' ? `${device.rssi} dBm` : '—'}
        </span>
        <span>Batería</span>
        <span className="text-right">
          {typeof device.battery_level === 'number' ? `${device.battery_level}%` : '—'}
        </span>
        <span>Streams</span>
        <span className="text-right">{streams.length ? streams.join(', ') : '—'}</span>
        <span>Actualizado</span>
        <span className="text-right">{formatTimestamp(lastUpdate)}</span>
      </div>

      {device.last_error && (
        <div className="rounded-md border border-rose-500/30 bg-rose-500/10 px-sm py-xs text-xs text-rose-400">
          {device.last_error}
        </div>
      )}

      <DataViewSelector mode={dataViewMode} onChange={setDataViewMode} />

      {dataViewMode !== 'none' && !hasData && (
        <div className="text-xs text-text-muted">Sin data (conectar primero).</div>
      )}

      {dataViewMode === 'raw' && hasData && (
        <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md border border-surface-muted bg-surface px-sm py-xs font-mono text-[11px] text-text-muted">
          {JSON.stringify(displayData, null, 2)}
        </pre>
      )}

      {dataViewMode === 'structured' && hasData && (
        <div className="rounded-md border border-surface-muted bg-surface px-sm py-xs">
          <BleStructuredDataView data={displayData} />
        </div>
      )}

      <div className="flex justify-end gap-sm">
        {device.connected ? (
          <button
            type="button"
            className="btn-ghost btn-sm"
            disabled={disabled}
            onClick={() => onDisconnect(device)}
          >
            Desconectar
          </button>
        ) : (
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={disabled}
            onClick={() => onConnect(device)}
          >
            Conectar
          </button>
        )}
      </div>
    </div>
  )
}
